import React, { FC, ChangeEvent, FocusEvent, KeyboardEvent } from "react";
import { ErrorMessage } from "@hookform/error-message";
import { Controller } from "react-hook-form";
import { Form } from "react-bootstrap";
import './TextField.scss';

interface TextFieldProps {
  formMethod: any;
  errors: any;
  value?: any;
  label?: any;
  placeholder?: string;
  type: string;
  showPassword?: boolean;
  togglePasswordVisibility?: () => void;
  textarea?: string;
  name: string;
  autoFocus?: boolean;
  multiErrorFields?: any;
  disabled?: boolean;
  handleFocus?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleBlur?: () => void;
  maxLength?: number;
  minLength?: number;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  rules: any;
  rows?: number;
  defaultValue?: string;
  readOnly?: boolean;
  required?: boolean;
  min?: number;
  max?: number;
  onClick?: () => void;
  ref?: React.RefObject<HTMLInputElement | HTMLTextAreaElement>;
  onChange?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  countVisible?: boolean;
  count?: any;
  countTotal?: number;
  isReqired?: boolean;
  titleText?: string;
  title?: boolean;
  className?: any;
  emailRepeat?: boolean;
}

const TextField: FC<TextFieldProps> = (props) => {
  const {
    errors,
    label,
    placeholder,
    multiErrorFields,
    disabled,
    type,
    textarea = false,
    name,
    autoFocus,
    handleFocus,
    handleBlur,
    maxLength,
    minLength,
    onKeyDown,
    formMethod,
    rows,
    defaultValue,
    rules,
    readOnly,
    required,
    min,
    max,
    onClick,
    onChange,
    showPassword,
    togglePasswordVisibility,
    countVisible,
    count,
    countTotal,
    isReqired = true,
    titleText,
    title,
    className,
    emailRepeat = false,
  } = props;

  const hasError = errors[name] !== undefined;

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (textarea && e.key === "Enter") {
      return; // Allow default behavior, which is to add a new line
    } else if (e.key === "Enter") {
      (e.target as HTMLInputElement | HTMLTextAreaElement).blur();
    }
    onKeyDown && onKeyDown(e);
  };

  return (
    <div className={className + " formGroup"}>
      <Controller
        defaultValue={defaultValue}
        render={({ field: { onChange, value } }) => (
          <>
            {label && (
              <label className="formlabel">
                {label}
              </label>
            )}
            {title && <span className="lightTextTitle">{titleText}</span>}
            <div className={type === "password" ? "group inline" : "group"}>
              <Form.Control
                as={textarea ? "textarea" : "input"}
                className={`${hasError ? "is-invalid" : ""} ${
                  value &&
                  rules.pattern &&
                  rules.pattern.value &&
                  rules.pattern.value.test(value)
                    ? "regex-match-class"
                    : ""
                } 
                ${emailRepeat ? "is-invalid" : ""}`} // for repeate email css
                name={name}
                placeholder={placeholder}
                autoFocus={autoFocus}
                disabled={disabled}
                autoComplete="off"
                required={required}
                type={type === "password" && showPassword ? "text" : type}
                readOnly={readOnly}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                maxLength={maxLength}
                minLength={minLength}
                {...(textarea ? { rows } : { min, max })}  
                onFocus={(e) => {
                  handleFocus && handleFocus(e);
                }}
                defaultValue={defaultValue}
                onChange={(e) => {
                  onChange && onChange(e.target.value.trimLeft());
                  props.onChange && props.onChange(e);
                }}
                onClick={onClick}
                value={formMethod.watch(name) || value ? value : ""}
              />
              {countVisible && (
                <span className="count">
                  {count} / {countTotal}
                </span>
              )}
              {type === "password" && (
                <div
                  onClick={togglePasswordVisibility}
                  className={`togglePasswordButton ${
                    !value ? "disabled" : ""
                  } ${hasError ? "invalid" : ""}`}
                >
                  {showPassword ? (
                    <i className="icon icon-visibility" />
                  ) : (
                    <i className="icon icon-visibility_off" />
                  )}
                </div>
              )}
            </div>
          </>
        )}
        name={name}
        control={formMethod?.control}
        rules={rules}
      />
      {multiErrorFields?.length > 0 ? (
        <ErrorMessage
          errors={errors}
          name={name}
          render={({ messages }) => {
            if (messages) {
              const isMultipleError = Object.keys(messages).every(
                (errKey) =>
                  multiErrorFields.filter(
                    (m: { [x: string]: undefined }) => m[errKey] !== undefined
                  ).length > 0
              );
              if (isMultipleError) {
                const arr: JSX.Element[] = [];
                for (const fieldError of multiErrorFields) {
                  const key = Object.keys(fieldError)[0];
                  const success = Object.keys(messages).includes(key);
                  arr.push(
                    <span className={success ? "red" : "green"} key={key}>
                      {fieldError[key]}
                    </span>
                  );
                }
                return (
                  <div className="errorMsg 2 show passwordcustom">{arr}</div>
                );
              } else {
                return (
                  <div className="errorMsg 1 show">
                    <i className="icon-error_outline" />
                    {errors[name]?.message}
                  </div>
                );
              }
            } else {
              return <div className="errorMsg 3"></div>;
            }
          }}
        />
      ) : hasError ? (
        <div className="errorMsg 4 show">
          <span>{errors[name]?.message}</span>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

TextField.defaultProps = {
  autoFocus: false,
  value: "",
  errors: {},
  multiErrorFields: [],
  readOnly: false,
  required: true,
};

export default TextField;